/**
 * Exact money arithmetic on integer paisa (1 BDT = 100 paisa).
 *
 * The wire carries decimal strings with two places ("12500.00", API_CONTRACT §2.8 / BD-13). Parsing them with
 * `Number()` and multiplying by a rate re-introduces binary float error (30.299999999999997), so every sum,
 * difference and percentage in the domain goes through integer paisa and is converted to taka only at the
 * display edge. Rounding matches the backend `Money.splitFee` policy: round half up to 2 decimal places.
 */

/** Integer paisa. Negative values are allowed (adjustments). */
export type Paisa = number;

const WIRE_MONEY = /^(-)?(\d+)(?:\.(\d{1,2}))?$/;

/**
 * Parse a wire value (decimal string or an already-numeric taka amount) to integer paisa.
 * Strings are parsed digit by digit — never through a float. Unusable input yields 0, like the old adapter.
 */
export function toPaisa(value: unknown): Paisa {
  if (typeof value === 'string') {
    const m = WIRE_MONEY.exec(value.trim());
    if (!m) return 0;
    const paisa = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0'));
    return m[1] ? -paisa : paisa;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    // `+ EPSILON` keeps 1.005-style inputs on the side a person would expect.
    return Math.round((Math.abs(value) + Number.EPSILON) * 100) * Math.sign(value);
  }
  return 0;
}

/** Integer paisa → the wire format "12500.00". */
export function paisaToWire(paisa: Paisa): string {
  const sign = paisa < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(paisa));
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/** Integer paisa → taka as a number, for display and for the component-facing DTOs. */
export function paisaToTaka(paisa: Paisa): number {
  return paisa / 100;
}

/** Taka number → integer paisa. */
export function takaToPaisa(taka: number): Paisa {
  return toPaisa(taka);
}

/**
 * `percent`% of an amount, rounded half up to the paisa (non-negative amounts).
 * 5% of 1234.50 → 61.73, not 61.72 — the backend `splitFee` policy.
 */
export function percentOfPaisa(paisa: Paisa, percent: number): Paisa {
  return Math.floor((paisa * percent + 50) / 100);
}
